import { api } from "../api/api";
import { SearchQuery, createSingleQuery } from "../api/search";

/**
* setSelectList: Prepare data to be printed in select
* @param data - data list
* @param setLabel - set label function
* TODO : recuperer l'information depuis l'API
* @return - prepared data list 
*/
export const isOdooConnected = (): boolean => {
  const realm = localStorage.getItem("realm");
  if (realm === "be" || realm === "lu" || realm === "ro" || realm === "pl")
    return true;
  return false;
};
  /***/

/**
* hasExternalId: cet enregistrement est-il relié à Odoo ?
*
* `externalId` est typé `number | string` : un test de véracité lirait l'identifiant parfaitement
* valide `0` comme « jamais synchronisé ». Seuls null, undefined et la chaîne vide valent absence.
*
* Extrait de InformationsTab, qui le portait seul, maintenant que les listes clients et pièces ainsi
* que le détail d'une pièce posent la même question. (Le badge « Envoyer vers Odoo » de Customer.tsx
* utilise toujours `!!externalId` ; ce comportement n'est pas touché ici.)
*
* @param externalId - identifiant Odoo éventuel
*/
export const hasExternalId = (externalId?: number | string | null): boolean =>
  externalId !== undefined && externalId !== null && externalId !== "";
/***/

/**
* odooSyncQueries: traduit les deux filtres Odoo d'une liste en requêtes de recherche.
*
* Les listes pièces et clients posent exactement les deux mêmes questions — « quel identifiant
* Odoo ? » et « lesquels n'ont jamais été synchronisés ? » — et la réponse porte deux subtilités
* qu'une deuxième copie serait l'occasion de perdre :
*
*  - les deux filtres s'excluent. Combinés en ET, ils demanderaient les enregistrements sans
*    identifiant portant un identifiant donné, soit toujours zéro résultat — et une liste vide ne
*    se distingue pas d'une recherche sans résultat. Les formulaires empêchent déjà de les poser
*    ensemble ; la règle est répétée ici parce que l'export appelle ces fonctions avec ce qu'il
*    trouve dans l'état, sans repasser par le formulaire ;
*  - `IS_NULL` plutôt qu'une comparaison à vide : c'est l'absence d'identifiant que « non
*    synchronisé » désigne, et c'est la seule forme d'absence que l'API sait interroger.
*
* Recherche exacte, pas `LIKE` : `externalId` est un identifiant, et rien ne garantit qu'il soit
* stocké en texte — un `LIKE` sur une colonne numérique échouerait côté API.
*
* @param filters - valeurs actives des deux filtres Odoo
*/
export const odooSyncQueries = (filters: {
  externalId?: string,
  odooNotSynced?: boolean
}): SearchQuery[] => {
  if (filters.odooNotSynced) {
    return [createSingleQuery("externalId", null, "IS_NULL")];
  }

  const externalId = filters.externalId?.trim();
  return externalId ? [createSingleQuery("externalId", externalId)] : [];
};
/***/

/**
* saveExternalId: écrit l'identifiant Odoo d'un client ou d'une pièce.
*
* Réservé au super admin, et sur son propre verbe : le PUT ordinaire ne peut pas déplacer ce lien.
* Côté core, `CustomerService.merge` recopie l'identifiant stocké par-dessus celui de la requête et
* `PartService.preUpdate` fait de même — c'est ce qui empêche une synchronisation Odoo ou un
* écrivain partiel de le déplacer par inadvertance. `admin/{ressource}/{id}/external-id` est la
* seule porte qui l'ouvre, et elle demande ROLE_SUPER_ADMIN.
*
* N'appelle rien quand la valeur n'a pas bougé : un simple changement de tarif ne doit pas réécrire
* le lien Odoo, ni pousser une resynchronisation vers les mobiles.
*
* À appeler AVANT l'enregistrement principal : un identifiant déjà pris répond 409, et échouer
* d'abord laisse la fiche entièrement intacte et le tiroir ouvert sur la saisie de l'utilisateur.
* L'enregistrement qui suit relit l'identifiant depuis la base et renvoie donc la nouvelle valeur.
*
* Une valeur vide délie la fiche : elle redevient « non synchronisée ».
*
* @param resource - "customers" ou "parts"
* @param id - identifiant EasyTech de la fiche
* @param previous - identifiant Odoo actuellement stocké
* @param next - identifiant Odoo saisi
* @return - vrai si un appel a été fait
*/
export const saveExternalId = async (
  resource: "customers" | "parts",
  id: string,
  previous?: number | string | null,
  next?: number | string | null
): Promise<boolean> => {
  const asText = (value?: number | string | null): string =>
    value === undefined || value === null ? "" : String(value).trim();

  const wanted = asText(next);
  if (wanted === asText(previous)) return false;

  await api.put(`admin/${resource}/${id}/external-id`, { externalId: wanted });
  return true;
};
/***/
